import { useEffect, useMemo, useRef, useState } from "react";

function OptionSelect({
  id,
  label,
  value,
  options,
  placeholder = "Select option",
  disabled = false,
  onChange,
  getOptionLabel = (option) => option.label,
  getOptionValue = (option) => option.value,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  const normalizedOptions = useMemo(
    () =>
      options.map((option) => ({
        label: getOptionLabel(option),
        value: String(getOptionValue(option)),
      })),
    [getOptionLabel, getOptionValue, options]
  );
  const menuOptions = useMemo(
    () => [
      { label: placeholder, value: "", isPlaceholder: true },
      ...normalizedOptions,
    ],
    [normalizedOptions, placeholder]
  );

  const selectedIndex = menuOptions.findIndex(
    (option) => option.value === String(value)
  );
  const selectedOption =
    selectedIndex >= 0 && menuOptions[selectedIndex].value
      ? menuOptions[selectedIndex]
      : null;

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;

    const activeOption = listRef.current?.querySelector(
      `[data-option-index="${activeIndex}"]`
    );
    activeOption?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, isOpen]);

  const openSelect = () => {
    if (disabled) return;
    setIsOpen(true);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
  };

  const closeSelect = () => {
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;

    if (!isOpen && ["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
      event.preventDefault();
      openSelect();
      return;
    }

    if (!isOpen) return;

    if (event.key === "Escape") {
      event.preventDefault();
      closeSelect();
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) =>
        Math.min(current + 1, menuOptions.length - 1)
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (activeIndex >= 0) {
        onChange(menuOptions[activeIndex].value);
        closeSelect();
      }
    }
  };

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    closeSelect();
  };

  return (
    <div ref={containerRef} className="relative flex flex-col gap-1.5">
      {label && (
        <label
          id={`${id}-label`}
          className="text-xs font-bold text-text-muted uppercase tracking-wider"
        >
          {label}
        </label>
      )}

      <button
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={`${id}-listbox`}
        aria-activedescendant={
          isOpen && activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined
        }
        aria-labelledby={label ? `${id}-label ${id}` : undefined}
        onClick={() => (isOpen ? closeSelect() : openSelect())}
        onKeyDown={handleKeyDown}
        className="group flex min-h-[46px] w-full items-center justify-between gap-3 rounded-xl border border-border bg-background px-4 py-3 text-left text-sm text-text shadow-sm outline-none transition-all duration-200 focus:border-transparent focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span
          className={`min-w-0 flex-1 truncate ${selectedOption ? "text-text" : "text-text-muted"
            }`}
        >
          {selectedOption?.label || placeholder}
        </span>
        <svg
          className={`h-4 w-4 shrink-0 text-text-muted transition-transform duration-200 ${isOpen ? "rotate-180" : ""
            }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="option-select-menu absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-xl border border-border bg-surface shadow-xl ring-1 ring-black/5">
          <ul
            id={`${id}-listbox`}
            ref={listRef}
            role="listbox"
            aria-labelledby={label ? `${id}-label` : undefined}
            tabIndex={-1}
            className="option-select-scroll max-h-64 overflow-y-auto overscroll-contain py-1"
          >
            {menuOptions.map((option, index) => {
              const isSelected = option.value === String(value);
              const isActive = index === activeIndex;

              return (
                <li
                  id={`${id}-option-${index}`}
                  key={option.value}
                  role="option"
                  aria-selected={isSelected}
                  data-option-index={index}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => handleSelect(option.value)}
                  className={`flex cursor-pointer items-start justify-between gap-3 rounded-lg px-4 py-3 text-sm leading-snug transition-all duration-200
                    hover:bg-[var(--color-surface-hover)]
                    ${isActive ? "bg-[var(--color-surface-hover)] text-text" : "text-text"}
                    ${option.isPlaceholder ? "text-text-muted" : ""}
                  `}
                >
                  <span className="min-w-0 break-words">{option.label}</span>
                  {isSelected && !option.isPlaceholder && (
                    <svg
                      className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2.5"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default OptionSelect;
